/* ApprovePayDialog.jsx — Doctor-side approval confirmation modal.
 *
 * iter98.99cq — Interactive Payments & Offers panel:
 *   · Tier headline + ETA
 *   · Charges breakdown
 *   · Promo & Offers — apply / change / remove a code inline
 *     (POST /cases/{id}/apply-promo)
 *   · Payment option — Pay later · Pay advance · Pay in full
 *     (POST /cases/{id}/advance-intent  /  /advance-intent/cancel)
 *   · Status pill for any existing live intent
 *   · Dynamic primary CTA — "Approve & ..." that, in one click:
 *       1. creates / cancels the advance intent per chosen mode,
 *       2. fires onConfirm() to PUT /cases/{id}/approve.
 *
 * Parent (PlanIterationsStack) supplies `caseId`, `pricing`, `onChange`
 * so the dialog can refresh case state after every server round-trip.
 */
import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Receipt, ShieldCheck, Tag, Wallet, CheckCircle, Hourglass,
    Clock, X as CloseIcon, ArrowRight, Lightning,
} from "@phosphor-icons/react";


function inr(n) {
    if (n == null || n === "") return "₹0";
    const v = Number(n);
    if (!Number.isFinite(v)) return "₹0";
    return `₹${Math.round(v).toLocaleString("en-IN")}`;
}


function rangeLabel(min, max) {
    const lo = Number(min || 0);
    const hi = Number(max || 0);
    if (hi <= 0) return "—";
    /* iter98.99da — Tier ranges expressed in STAGES (1 stage = 1 upper +
       1 lower aligner set). Industry-standard clinical progression unit. */
    if (lo > 0) return `${lo}–${hi} stages`;
    return `Up to ${hi} stages`;
}


export default function ApprovePayDialog({
    open, onOpenChange, caseId, pricing, busy, onConfirm, onChange,
}) {
    const p = pricing || {};
    const labCharge = Number(p.lab_charge || 0);
    const priorityFee = Number(p.priority_fee || 0);
    const discount = Number(p.discount_amount || 0);
    const scannerFee = Number(p.scanner_pickup_fee || 0);
    /* iter101.45 — Compliance Monitoring add-on. Same snapshot pattern
       as priority + scanner: fee locked in when the doctor opted in
       (Step 1 or post-approval upgrade). Missing snapshot on legacy
       cases falls back to 0 — the invoice line still surfaces from
       `_compliance_fee()` server-side, so no revenue leak. */
    const complianceFee = Number(p.invisique_managed_compliance_fee || 0);
    const finalCharge = Number(p.final_charge || 0)
        || Math.max(0, labCharge + priorityFee + scannerFee + complianceFee - discount);

    /* ─────────── Promo state ─────────── */
    const promoApplied = !!(p.promo_id || p.promo_code);
    const [promoCode, setPromoCode] = useState("");
    const [promoBusy, setPromoBusy] = useState(false);
    const [promos, setPromos] = useState([]);

    /* iter98.99cx — Resolve tier aligner range. Newer cases carry
       `tier_aligner_min` + `tier_aligner_count` in the pricing snapshot.
       For older cases we fall back to /tiers/active joined by tier_id. */
    const [tiersIndex, setTiersIndex] = useState(null);
    useEffect(() => {
        if (!open) return;
        const needsFetch = !!p.tier_id
            && (!p.tier_aligner_count || p.tier_aligner_count <= 0);
        if (!needsFetch) return;
        let cancelled = false;
        api.get("/tiers/active").then((r) => {
            if (cancelled) return;
            const list = Array.isArray(r.data) ? r.data : [];
            const idx = {};
            list.forEach((t) => { idx[t.id] = t; });
            setTiersIndex(idx);
        }).catch(() => { /* silent */ });
        return () => { cancelled = true; };
    }, [open, p.tier_id, p.tier_aligner_count]);
    const tierFromIndex = (tiersIndex && p.tier_id) ? tiersIndex[p.tier_id] : null;
    const resolvedTierMin = Number(p.tier_aligner_min ?? tierFromIndex?.aligner_min ?? 0);
    const resolvedTierMax = Number(
        p.tier_aligner_count
        ?? p.tier_aligner_max
        ?? tierFromIndex?.aligner_count
        ?? 0
    );

    /* ─────────── Advance intent state ─────────── */
    const [intent, setIntent] = useState(null);     // { status, amount, link_url, ... } or null
    const [intentLoading, setIntentLoading] = useState(false);
    const [paymentMode, setPaymentMode] = useState("later");  // 'later' | 'advance' | 'full'
    const [advanceAmount, setAdvanceAmount] = useState("");
    const [committing, setCommitting] = useState(false);

    /* Fetch eligible promos + current intent whenever dialog opens.
       Reset all transient inputs each open so re-opening doesn't carry
       stale state from a previous visit. */
    useEffect(() => {
        if (!open || !caseId) return;
        let cancelled = false;

        setPromoCode("");
        api.get("/promotions/active").then((r) => {
            if (!cancelled) setPromos(Array.isArray(r.data) ? r.data : []);
        }).catch(() => { /* silent */ });

        setIntentLoading(true);
        api.get(`/cases/${caseId}/advance-intent`).then((r) => {
            if (cancelled) return;
            const d = r.data || {};
            const live = (d && d.status && d.status !== "none" && d.status !== "cancelled") ? d : null;
            setIntent(live);
            if (live?.status === "paid") {
                setPaymentMode("advance");                  // already paid – treat as advance
                setAdvanceAmount(String(Math.round(Number(live.amount) || 0)));
            } else if (live && Number(live.amount) >= finalCharge && finalCharge > 0) {
                setPaymentMode("full");
                setAdvanceAmount(String(Math.round(finalCharge)));
            } else if (live) {
                setPaymentMode("advance");
                setAdvanceAmount(String(Math.round(Number(live.amount) || 0)));
            } else {
                /* No live intent — seed defaults from the case's pricing
                   (the wizard may have captured advance_amount at Step 1
                   even though no intent has been minted yet). */
                const seedAdvance = Number(p.advance_amount || 0);
                if (seedAdvance > 0) {
                    setPaymentMode("advance");
                    setAdvanceAmount(String(Math.round(seedAdvance)));
                } else {
                    setPaymentMode("later");
                    /* Default suggestion: 30% of total, rounded to nearest 1000. */
                    const suggested = Math.max(0, Math.round((finalCharge * 0.3) / 1000) * 1000);
                    setAdvanceAmount(suggested ? String(suggested) : "");
                }
            }
        }).catch(() => { /* silent */ })
        .finally(() => { if (!cancelled) setIntentLoading(false); });

        return () => { cancelled = true; };
    }, [open, caseId, finalCharge, p.advance_amount]);


    /* ─────────── Promo handlers ─────────── */
    const applyPromo = async (rawCode) => {
        const clean = (rawCode || "").trim().toUpperCase();
        if (!clean) return;
        setPromoBusy(true);
        try {
            await api.post(`/cases/${caseId}/apply-promo`, { code: clean });
            toast.success(`Applied: ${clean}`);
            setPromoCode("");
            if (typeof onChange === "function") await onChange();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "That code is not valid");
        } finally {
            setPromoBusy(false);
        }
    };

    const clearPromo = async () => {
        setPromoBusy(true);
        try {
            await api.post(`/cases/${caseId}/apply-promo`, { code: null });
            toast.success("Promo removed");
            if (typeof onChange === "function") await onChange();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not remove the promo");
        } finally {
            setPromoBusy(false);
        }
    };

    const eligiblePromos = promos.filter((pr) => {
        if (!p.tier_id) return false;
        const list = pr.applicable_tier_ids || [];
        return list.length === 0 || list.includes(p.tier_id);
    });


    /* ─────────── Approve + commit handler ─────────── */
    const intentStatus = intent?.status || null;
    const intentPaid = intentStatus === "paid";

    const targetAmountForMode = () => {
        if (paymentMode === "full") return finalCharge;
        if (paymentMode === "advance") return Math.max(0, Number(advanceAmount || 0));
        return 0;
    };

    const handleApprove = async () => {
        if (committing || busy) return;
        const targetAmt = targetAmountForMode();
        setCommitting(true);
        try {
            if (paymentMode === "later") {
                /* If there's a pending / link_sent intent, cancel it
                   to keep the case state consistent. A paid intent is
                   left alone — money already moved. */
                if (intentStatus && intentStatus !== "paid") {
                    try {
                        await api.post(`/cases/${caseId}/advance-intent/cancel`);
                    } catch { /* silent — endpoint 404s if nothing to cancel */ }
                }
            } else if (!intentPaid) {
                /* Create or replace the intent. The backend soft-cancels
                   any prior pending intent so this is idempotent. */
                if (!(targetAmt > 0)) {
                    toast.error("Enter a valid amount to pay");
                    setCommitting(false);
                    return;
                }
                const pct = finalCharge > 0
                    ? Math.min(100, Math.round((targetAmt / finalCharge) * 100))
                    : 0;
                await api.post(`/cases/${caseId}/advance-intent`, {
                    amount: targetAmt,
                    pct,
                });
            }
            /* Finally fire the approval — caller owns the toast + reload. */
            await onConfirm();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't record your choice");
        } finally {
            setCommitting(false);
        }
    };

    /* CTA label reflects the doctor's chosen mode. */
    const ctaLabel = (() => {
        if (committing || busy) return "Working…";
        if (intentPaid) return `Approve & commit ${inr(finalCharge)}`;
        if (paymentMode === "full") return `Pay ${inr(finalCharge)} & approve`;
        if (paymentMode === "advance") {
            const a = Number(advanceAmount || 0);
            return a > 0 ? `Pay ${inr(a)} advance & approve` : "Approve plan";
        }
        return "Approve & bill on shipment";
    })();

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!busy && !committing) onOpenChange(v); }}>
            <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden gap-0" data-testid="approve-pay-dialog">
                <DialogHeader className="px-6 pt-5 pb-3 border-b border-border bg-gradient-to-br from-emerald-50/60 via-white to-amber-50/30">
                    <DialogTitle className="font-display text-xl inline-flex items-center gap-2">
                        <ShieldCheck size={22} weight="fill" className="text-emerald-600" />
                        Finalize &amp; Approve Plan
                    </DialogTitle>
                    <div className="text-[12px] text-muted-foreground mt-1 leading-snug">
                        Confirm the treatment plan, activate any available discounts, select your payment terms, and authorize fabrication to begin.
                    </div>
                </DialogHeader>

                <div className="px-6 py-5 space-y-4 max-h-[68vh] overflow-y-auto">

                    {/* ── TIER HEADLINE ── */}
                    <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 px-4 py-3.5">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-[9px] font-bold tracking-[0.18em] uppercase text-emerald-700">Treatment tier</div>
                                <div className="font-display text-xl font-bold leading-tight mt-0.5" data-testid="approve-pay-tier-name">
                                    {p.tier_name || "—"}
                                </div>
                                <div className="text-[12px] text-emerald-800/80 mt-0.5">
                                    {rangeLabel(resolvedTierMin, resolvedTierMax)}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="text-[9px] font-bold tracking-[0.16em] uppercase text-emerald-700/70">Lab charge</div>
                                <div className="font-mono font-bold text-lg text-emerald-900 mt-0.5" data-testid="approve-pay-lab-charge">{inr(labCharge)}</div>
                            </div>
                        </div>
                        {p.priority_label && p.priority_code && p.priority_code !== "standard" && (
                            <div className="mt-2.5 pt-2.5 border-t border-emerald-200/60 flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-[10px] font-bold uppercase tracking-[0.12em] text-amber-800">
                                    <Clock size={10} weight="fill" /> {p.priority_label}
                                </span>
                                {p.priority_tat_days && (
                                    <span className="text-[11px] text-emerald-800/80">{p.priority_tat_days}-day TAT</span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── CHARGES BREAKDOWN ── */}
                    <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <div className="text-[9px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-2.5 inline-flex items-center gap-1.5">
                            <Receipt size={11} weight="fill" /> Charges breakdown
                        </div>
                        <div className="space-y-1.5">
                            <Line k="Lab charge" v={inr(labCharge)} />
                            {/* iter98.99cx — Manufacturing speed ALWAYS shown
                                so the dialog matches the Plan Summary row
                                ("Express TAT + ₹5,000" vs "Standard TAT
                                Included"). Previously hidden when fee=0,
                                which made Plan Summary's "Included" copy
                                look like the dialog had lost the data. */}
                            {p.priority_label && (
                                <Line
                                    k={
                                        <span className="inline-flex items-center gap-1.5">
                                            <Clock size={11} weight="duotone" className="text-foreground/50" />
                                            Manufacturing speed
                                            <span className="text-muted-foreground/80 font-normal">
                                                · {p.priority_label}
                                                {p.priority_tat_days ? ` (${p.priority_tat_days}-day TAT)` : ""}
                                            </span>
                                        </span>
                                    }
                                    v={
                                        priorityFee > 0
                                            ? inr(priorityFee)
                                            : <span className="text-muted-foreground italic text-[12px]">Included</span>
                                    }
                                />
                            )}
                            {/* iter98.99cx — IO Scanning Service ALWAYS shown
                                when the doctor opted in, even if the fee is
                                rolled into a promotion or tier. */}
                            {p.scanner_pickup_requested && (
                                <Line
                                    k={
                                        <span className="inline-flex items-center gap-1.5">
                                            IO Scans by Invisique™
                                        </span>
                                    }
                                    v={
                                        scannerFee > 0
                                            ? inr(scannerFee)
                                            : <span className="text-muted-foreground italic text-[12px]">Included</span>
                                    }
                                />
                            )}
                            {/* iter101.45 — Compliance Monitoring add-on line.
                                Renders whenever the case has a snapshotted
                                fee (the opt-in path stamps this on the
                                pricing block). "Included" fallback keeps
                                the row honest when the admin has published
                                a ₹0 monitoring fee. */}
                            {complianceFee > 0 && (
                                <Line
                                    k={
                                        <span className="inline-flex items-center gap-1.5">
                                            <ShieldCheck size={11} weight="duotone" className="text-primary/70" />
                                            Compliance Monitoring &amp; Support
                                        </span>
                                    }
                                    v={inr(complianceFee)}
                                />
                            )}
                            {discount > 0 && (
                                <Line
                                    k={
                                        <span className="inline-flex items-center gap-1.5">
                                            <Tag size={11} weight="fill" className="text-emerald-600" />
                                            Promo discount {p.promo_code ? `· ${p.promo_code}` : ""}
                                        </span>
                                    }
                                    v={`− ${inr(discount)}`}
                                    discount
                                />
                            )}
                        </div>
                        <div className="border-t border-border pt-2.5 mt-2.5 flex items-center justify-between">
                            <span className="text-sm font-bold">Total payable</span>
                            <span className="font-display text-xl font-bold tabular-nums" data-testid="approve-pay-total">{inr(finalCharge)}</span>
                        </div>
                    </div>

                    {/* ── PROMO & OFFERS — interactive ── */}
                    <div className="rounded-xl border border-emerald-200/70 bg-emerald-50/30 px-4 py-3" data-testid="approve-pay-promo-section">
                        <div className="flex items-center justify-between gap-2 mb-2.5">
                            <div className="text-[9px] font-bold tracking-[0.18em] uppercase text-emerald-800 inline-flex items-center gap-1.5">
                                <Tag size={11} weight="fill" /> Promo &amp; offers
                            </div>
                            {promoBusy && <span className="text-[10px] text-muted-foreground">Updating…</span>}
                        </div>

                        {promoApplied ? (
                            <div className="flex items-center justify-between gap-3" data-testid="approve-pay-promo-applied">
                                <div className="flex items-center gap-2 min-w-0">
                                    <CheckCircle size={14} weight="fill" className="text-emerald-700 shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-[12.5px] font-semibold text-emerald-900 truncate">
                                            <span className="font-mono">{p.promo_code}</span> applied
                                            {discount > 0 && (
                                                <span className="text-emerald-800/85 font-normal"> · saved {inr(discount)}</span>
                                            )}
                                        </div>
                                        {p.discount_type && (
                                            <div className="text-[11px] text-emerald-800/80 leading-snug">
                                                {p.discount_type === "percentage"
                                                    ? `${p.discount_value}% off lab charge`
                                                    : `${inr(p.discount_value)} off lab charge`}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={clearPromo}
                                    disabled={promoBusy}
                                    className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-50 px-2 py-1 rounded-md transition-colors disabled:opacity-50"
                                    data-testid="approve-pay-promo-clear"
                                >
                                    <CloseIcon size={11} weight="bold" /> Remove
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2.5">
                                {eligiblePromos.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5" data-testid="approve-pay-promo-chips">
                                        {eligiblePromos.map((pr) => (
                                            <button
                                                key={pr.id}
                                                type="button"
                                                disabled={promoBusy}
                                                onClick={() => applyPromo(pr.code)}
                                                data-testid={`approve-pay-promo-chip-${pr.code}`}
                                                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100/70 hover:bg-emerald-200/80 ring-1 ring-emerald-300 px-2.5 py-1 text-[11.5px] font-semibold text-emerald-800 transition disabled:opacity-50"
                                            >
                                                <Tag size={10} weight="fill" />
                                                {pr.code} · {pr.discount_type === "percentage" ? `${pr.discount_value}% off` : `${inr(pr.discount_value)} off`}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div className="flex gap-2">
                                    <Input
                                        value={promoCode}
                                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                        placeholder="Type a promo code"
                                        className="h-9 text-[12.5px] font-mono uppercase tracking-wider"
                                        data-testid="approve-pay-promo-input"
                                        disabled={promoBusy}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(promoCode); } }}
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => applyPromo(promoCode)}
                                        disabled={promoBusy || !promoCode.trim()}
                                        className="h-9 px-3.5 text-[12.5px] border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                                        data-testid="approve-pay-promo-apply"
                                    >
                                        Apply
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── PAYMENT OPTIONS ── */}
                    <div className="rounded-xl border border-border bg-card px-4 py-3.5" data-testid="approve-pay-payment-section">
                        <div className="flex items-center justify-between gap-2 mb-2.5">
                            <div className="text-[9px] font-bold tracking-[0.18em] uppercase text-foreground/70 inline-flex items-center gap-1.5">
                                <Wallet size={11} weight="fill" /> How would you like to pay?
                            </div>
                            {intentLoading && <span className="text-[10px] text-muted-foreground">Loading…</span>}
                        </div>

                        {intentPaid ? (
                            <div
                                className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2.5 flex items-center gap-2.5"
                                data-testid="approve-pay-intent-paid"
                            >
                                <CheckCircle size={16} weight="fill" className="text-emerald-700 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[12.5px] font-semibold text-emerald-900">
                                        Advance of {inr(intent?.amount)} received
                                    </div>
                                    <div className="text-[11px] text-emerald-800/80 leading-snug">
                                        Balance of {inr(Math.max(0, finalCharge - Number(intent?.amount || 0)))} will be billed on shipment.
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Payment option">
                                    <ModeButton
                                        active={paymentMode === "later"}
                                        onClick={() => setPaymentMode("later")}
                                        icon={Clock}
                                        title="Pay later"
                                        subtitle="Bill on shipment"
                                        testId="approve-pay-mode-later"
                                    />
                                    <ModeButton
                                        active={paymentMode === "advance"}
                                        onClick={() => setPaymentMode("advance")}
                                        icon={Hourglass}
                                        title="Pay advance"
                                        subtitle="Pick amount"
                                        testId="approve-pay-mode-advance"
                                    />
                                    <ModeButton
                                        active={paymentMode === "full"}
                                        onClick={() => {
                                            setPaymentMode("full");
                                            setAdvanceAmount(String(Math.round(finalCharge)));
                                        }}
                                        icon={Lightning}
                                        title="Pay in full"
                                        subtitle={inr(finalCharge)}
                                        testId="approve-pay-mode-full"
                                    />
                                </div>

                                {/* Advance amount input — only when 'advance' is chosen */}
                                {paymentMode === "advance" && (
                                    <div className="mt-3 space-y-1.5" data-testid="approve-pay-advance-config">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-sm text-muted-foreground">₹</span>
                                            <Input
                                                value={advanceAmount}
                                                onChange={(e) => setAdvanceAmount(e.target.value.replace(/[^\d]/g, ""))}
                                                placeholder="0"
                                                inputMode="numeric"
                                                className="h-9 text-sm font-mono tabular-nums"
                                                data-testid="approve-pay-advance-input"
                                            />
                                            {finalCharge > 0 && (
                                                <span className="text-[10.5px] text-muted-foreground shrink-0 tabular-nums">
                                                    {Math.min(100, Math.round((Number(advanceAmount || 0) / finalCharge) * 100))}% of total
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {[25, 50, 75].map((pct) => (
                                                <button
                                                    key={pct}
                                                    type="button"
                                                    onClick={() => setAdvanceAmount(
                                                        String(Math.max(0, Math.round((finalCharge * pct) / 100)))
                                                    )}
                                                    className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full border border-border hover:bg-secondary/40 text-foreground/70"
                                                    data-testid={`approve-pay-advance-quick-${pct}`}
                                                >
                                                    {pct}%
                                                </button>
                                            ))}
                                        </div>
                                        {Number(advanceAmount || 0) > finalCharge && (
                                            <div className="text-[11px] text-rose-700">
                                                Advance can&apos;t be more than the total ({inr(finalCharge)}).
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Live intent recap (pending / link_sent) — only when relevant */}
                                {intentStatus && intentStatus !== "paid" && (
                                    <div
                                        className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 flex items-center gap-2 text-[11.5px] text-amber-900"
                                        data-testid="approve-pay-intent-pending"
                                    >
                                        <Hourglass size={13} weight="duotone" className="text-amber-700 shrink-0" />
                                        <div className="flex-1 min-w-0 leading-snug">
                                            A {inr(intent?.amount)} pay-link is currently {intentStatus === "link_sent" ? "out with your team" : "pending"}.
                                            {paymentMode !== "later" && " It will be replaced when you confirm below."}
                                            {paymentMode === "later" && " It will be cancelled when you confirm below."}
                                        </div>
                                    </div>
                                )}

                                {/* Pay-link affordance (only after intent exists + not paid + not later) */}
                                {intent?.link_url && intentStatus && intentStatus !== "paid" && paymentMode !== "later" && (
                                    <a
                                        href={intent.link_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-primary hover:underline"
                                        data-testid="approve-pay-intent-link"
                                    >
                                        Open existing pay-link <ArrowRight size={11} weight="bold" />
                                    </a>
                                )}
                            </>
                        )}
                    </div>

                    {/* ── FINE PRINT ── */}
                    <div className="text-[11px] text-muted-foreground leading-relaxed flex items-start gap-2">
                        <Clock size={12} weight="duotone" className="text-foreground/40 shrink-0 mt-0.5" />
                        <span>
                            Manufacturing kicks off as soon as your planning team finalises the spec.
                            You&apos;ll get a notification when aligners ship.
                        </span>
                    </div>
                </div>

                <DialogFooter className="gap-2 px-6 py-4 border-t border-border bg-slate-50/40">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={busy || committing}
                        data-testid="approve-pay-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApprove}
                        disabled={busy || committing
                            || (paymentMode === "advance" && !intentPaid && (
                                !(Number(advanceAmount || 0) > 0) || Number(advanceAmount || 0) > finalCharge
                            ))}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="approve-pay-confirm"
                    >
                        {ctaLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─────────── Small subcomponents ─────────── */

function Line({ k, v, discount }) {
    return (
        <div className="flex items-center justify-between text-[12.5px]">
            <span className="text-muted-foreground">{k}</span>
            <span className={`font-mono tabular-nums ${discount ? "text-emerald-700" : "text-foreground/90"}`}>{v}</span>
        </div>
    );
}

function ModeButton({ active, onClick, icon: Icon, title, subtitle, testId }) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={active}
            onClick={onClick}
            data-testid={testId}
            className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                active
                    ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200 shadow-sm"
                    : "border-border bg-card hover:border-emerald-200 hover:bg-emerald-50/40"
            }`}
        >
            <div className={`inline-flex items-center gap-1.5 text-[12px] font-bold leading-tight ${active ? "text-emerald-800" : "text-foreground/85"}`}>
                <Icon size={13} weight={active ? "fill" : "duotone"} />
                {title}
            </div>
            <div className={`text-[10.5px] mt-0.5 leading-tight ${active ? "text-emerald-700/85" : "text-muted-foreground"}`}>
                {subtitle}
            </div>
        </button>
    );
}
